import format from 'date-fns/format'
import add from 'date-fns/add'
import sub from 'date-fns/sub'

import { Types, Services } from 'service-api'
import { TBookingContextDef } from '@providers/booking/booking'
import { sortOrders, buildPetGroups, buildPetGroupsTempered } from '@shared/booking_groups'
import { TRouteNames } from 'app/app.d'

export const continueNavigation = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  switch (bookingCtx.state.currentState) {
    // if we are on search results, we need to check if there are offers or petAddOns to know
    // where to jump next, to keep browser history clean
    case 'searchResults': {
      let rc = await checkOfferAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      rc = await checkPetAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      if (isOfferWithTemperTest(bookingCtx) && somePetNeedsTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      return 'detailsPolicies'
    }
    // if we are on offerAddOns, we need to check if there are petAddOns to jump to that page
    // or skip it
    case 'roomAddOns': {
      const rc = await checkPetAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      // if there are no petAddOns check if the offer requires temper test
      if (isOfferWithTemperTest(bookingCtx) && somePetNeedsTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      // if not move to details
      return 'detailsPolicies'
    }
    case 'petAddOns': {
      removePerksWithZeroQuantity(bookingCtx)
      // check if the offer requires temper test
      if (isOfferWithTemperTest(bookingCtx) && somePetNeedsTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      // if not move to details
      return 'detailsPolicies'
    }
    // dates selection for DayCare
    case 'datesSelection': {
      // if (!bookingCtx.state.groups?.[0]?.pets?.[0].temperTested) {
      if (isOfferWithTemperTest(bookingCtx) && somePetNeedsTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      return 'detailsPolicies'
    }
    case 'temperTest': {
      return 'detailsPolicies'
    }
    case 'detailsPolicies': {
      return 'paymentForm'
    }
  }

  return
}

const removePerksWithZeroQuantity = (bookingCtx: TBookingContextDef) => {
  bookingCtx.state.cart.groups.forEach((group, groupIdx) => {
    group.petsAddOn?.forEach((petAddOn) => {
      petAddOn.pets.forEach((pet) => {
        if (pet.qty === 0) {
          bookingCtx.dispatch({
            type: 'REMOVE_PET_ADDON',
            payload: {
              groupIdx,
              petAddOnId: petAddOn.offer.id,
              petId: pet.id,
            },
          })
        }
      })
    })
  })
}

const isOfferWithTemperTest = (bookingCtx: TBookingContextDef) => {
  return bookingCtx.state.cart.groups.some((g) => {
    return g.offerSelected?.availability.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
  })
}

const somePetNeedsTemperTest = (bookingCtx: TBookingContextDef) => {
  return bookingCtx.state.groups.some((g) => g.pets.some((p) => !p.temperTested))
}

const filterAvailableAddOns = (results: Types.BookingSearchClientDto[]) =>
  results.filter((addOn) =>
    addOn.availabilityGroups?.some((group) => group.availabilities?.some((av) => !!av.available))
  )

const checkOfferAddOns = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.kind
  const services = await Services.ClientBookingSearchService.search(accountName, serviceTypeName, {
    ...buildSearchParams(bookingCtx, true),
    offersType: 'SERVICE',
  })
  const availableAddOns = filterAvailableAddOns(services.results)
  if (availableAddOns.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_OFFERS_ADDONS',
      payload: { value: sortOrders(availableAddOns), searchState: 'roomAddOns' },
    })
    return 'searchRoomAddOns'
  }
  return
}

const checkPetAddOns = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.kind
  const petAddOns = await Services.ClientBookingSearchService.search(accountName, serviceTypeName, {
    ...buildSearchParams(bookingCtx, true),
    offersType: 'PET',
  })
  const availableAddOns = filterAvailableAddOns(petAddOns.results)
  if (availableAddOns.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_PET_ADDONS',
      payload: { value: sortOrders(availableAddOns), searchState: 'petAddOns' },
    })
    return 'searchPetAddOns'
  }
  return
}

export const buildSearchParams = (bookingCtx: TBookingContextDef, isAddOnSearch?: boolean) => ({
  startDate: format(bookingCtx.state.startDate || 0, 'yyyy-MM-dd'),
  endDate: format(bookingCtx.state.endDate || 0, 'yyyy-MM-dd'),
  petGroups: buildPetGroups(bookingCtx.state, isAddOnSearch),
})

export const buildSearchParamsForTemperTest = (
  bookingCtx: TBookingContextDef,
  maxDate: Date
): Types.TBookingSearchOptions => {
  const searchParams = {
    startDate: format(bookingCtx.state.startDate || 0, 'yyyy-MM-dd'),
    endDate: format(bookingCtx.state.endDate || 0, 'yyyy-MM-dd'),
    petGroups: buildPetGroupsTempered(bookingCtx.state),
  }

  // Lookup the TemperTest offer to get the price, using the same payload as a regular offer search
  const startDateStr =
    bookingCtx.state.cart.groups?.[0]?.offerSelected?.availability.availabilities?.[0].condition?.dependOnDate
  const startDate = startDateStr ? new Date(`${startDateStr}T00:00:00.000`) : new Date()
  const maxValue =
    bookingCtx.state.cart.groups?.[0]?.offerSelected?.availability.availabilities?.[0].condition?.reason?.max || 0
  const endDate = maxDate ? sub(add(maxDate, { days: 1 }), { days: Math.ceil(maxValue / 1440) }) : new Date()

  return {
    ...searchParams,
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
    offersType: 'PRIMARY',
  }
}
